import React from "react";
import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";

interface Props {
  /** Describes the data set — rendered as the table's `<caption>`. */
  caption: string;
  /** Column headers, first entry labelling the row-header column. */
  columns: string[];
  /** One array per row; the first cell becomes that row's `<th scope="row">`. */
  rows: React.ReactNode[][];
  /**
   * Brings the table on screen while it holds focus. Needed only when a row
   * carries a control that is the sole keyboard route to something the chart
   * offers by mouse — a focus indicator inside a clipped box is invisible
   * (WCAG 2.4.7). Requires a `position: relative` ancestor.
   */
  revealOnFocus?: boolean;
}

const revealedSx = {
  "&:focus-within": {
    clip: "auto",
    width: "auto",
    height: "auto",
    maxWidth: "100%",
    maxHeight: "100%",
    m: 0,
    p: 1.5,
    overflow: "auto",
    whiteSpace: "normal",
    zIndex: 3,
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    boxShadow: 3,
  },
};

const tableSx = {
  "& caption": { textAlign: "left", fontWeight: 700, pb: 1 },
  "& th, & td": {
    textAlign: "left",
    p: "2px 10px 2px 0",
    fontSize: "0.8125rem",
    whiteSpace: "nowrap",
  },
  "& thead th": { fontWeight: 700 },
};

/**
 * Text alternative for a chart (WCAG 1.1.1). Charts are marked `role="img"`
 * with a summary label, which collapses their SVG into a single node; this
 * table carries the per-point values that summary can't hold. It is hidden
 * from sight only — screen readers, find-in-page and copy still reach it.
 */
const ChartDataTable = ({
  caption,
  columns,
  rows,
  revealOnFocus = false,
}: Props) => (
  <Box
    sx={
      revealOnFocus
        ? { ...visuallyHidden, ...tableSx, ...revealedSx }
        : { ...visuallyHidden, ...tableSx }
    }
  >
    <table>
      <caption>{caption}</caption>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column} scope="col">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={`chart-data-row-${rowIndex}`}>
            {row.map((cell, cellIndex) =>
              cellIndex === 0 ? (
                <th key={`chart-data-cell-${cellIndex}`} scope="row">
                  {cell}
                </th>
              ) : (
                <td key={`chart-data-cell-${cellIndex}`}>{cell}</td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </Box>
);

export default ChartDataTable;
